import { lazy, Suspense } from "react";
import { LinearProgress } from "@mui/material";
import { Box } from "@mui/system";
import {
  createHashRouter,
  Outlet,
  Route,
  useNavigation,
} from "react-router-dom";
import { Contexts } from "../Contexts";
import {
  AuthRedirect,
  ForbidAuth,
  RequireAuth,
  SignInPage,
} from "../features/Authorization";
import { CheckEmailPage } from "../features/Authorization/CheckEmailPage";
import { ForgotPasswordPage } from "../features/Authorization/ForgotPasswordPage";
import { PasswordChangedPage } from "../features/Authorization/PasswordChangedPage";
import { SetPasswordPage } from "../features/Authorization/SetPasswordPage";
import { SignOutPage } from "../features/Authorization/SignOutPage";
import { SyncLocalFavorites } from "../features/Coaches/Favorites/useFavorites";
import { DashboardPage } from "../features/Dashboard";
import ErrorPage from "../features/ErrorHandling/ErrorPage";
import { rolesDefByRoute, routes } from "./constants";
import { isDevelopment } from "../utils/environment";

// Helper: React.lazy with named exports
const lazyNamed = (importFn, name) =>
  lazy(() => importFn().then((m) => ({ default: m[name] })));
const lazyDefault = (importFn) => lazy(() => importFn());

// Lazy-loaded feature pages
const AssessmentPage = lazyNamed(
  () => import("../features/Assessment"),
  "AssessmentPage",
);
const StrengthsPage = lazyNamed(
  () => import("../features/Strengths"),
  "StrengthsPage",
);
const SetValuesPage = lazyNamed(
  () => import("../features/Values"),
  "SetValuesPage",
);
const MyValuesPage = lazyNamed(
  () => import("../features/Values"),
  "MyValuesPage",
);
const SessionsPage = lazyNamed(
  () => import("../features/Sessions"),
  "SessionsPage",
);
const SessionPage = lazyNamed(
  () => import("../features/Sessions/Session"),
  "SessionPage",
);
const StartSessionPage = lazyNamed(
  () => import("../features/Sessions/StartSession.page"),
  "StartSessionPage",
);
const CoachesPage = lazyNamed(
  () => import("../features/Coaches/Coaches.page"),
  "CoachesPage",
);
const MySessionsPage = lazyNamed(
  () => import("../features/MySessions/MySessions.page"),
  "MySessionsPage",
);
const SettingsPage = lazyNamed(
  () => import("../features/Settings/Settings.page"),
  "SettingsPage",
);
const UserProfilePage = lazyNamed(
  () => import("../features/Settings/UserProfilePage"),
  "UserProfilePage",
);
const IntegrationResultPage = lazyNamed(
  () => import("../features/Integrations/IntegrationResultPage"),
  "IntegrationResultPage",
);
const HelpPage = lazyNamed(
  () => import("../features/Help/Help.page"),
  "HelpPage",
);
const GetFeedbackPage = lazyNamed(
  () => import("../features/Feedback/GetFeedback.page"),
  "GetFeedbackPage",
);
const CreateFeedbackPage = lazyNamed(
  () => import("../features/Feedback/CreateFeedback.page"),
  "CreateFeedbackPage",
);
const FeedbackResultsPage = lazyNamed(
  () => import("../features/Feedback/FeedbackResults.page"),
  "FeedbackResultsPage",
);
const ExternalFeedbackPage = lazyNamed(
  () => import("../features/Feedback/ExternalFeedbackPage"),
  "ExternalFeedbackPage",
);
const SessionsAndMembers = lazyNamed(
  () => import("../features/SessionsAndMembers/SessionsAndMembers.page"),
  "SessionsAndMembers",
);
const TeamPage = lazyNamed(
  () => import("../features/Team/Team.page"),
  "TeamPage",
);
const MyTeamPage = lazyNamed(
  () => import("../features/MyTeam/MyTeam"),
  "MyTeamPage",
);
const ClientsPage = lazyNamed(
  () => import("../features/Clients/ClientsPage"),
  "ClientsPage",
);
const MessagesPage = lazyNamed(
  () => import("../features/Messages/Messages.page"),
  "MessagesPage",
);
const NotesPage = lazyNamed(
  () => import("../features/Notes/NotesPage"),
  "NotesPage",
);
const ReportsPage = lazyNamed(
  () => import("../features/Reports/ReportsPage"),
  "ReportsPage",
);
const ArticlePage = lazyNamed(
  () => import("../features/Articles/ArticlePage"),
  "ArticlePage",
);
const HRProgramsPage = lazyNamed(
  () => import("../features/HRPrograms/HRPrograms.page"),
  "HRProgramsPage",
);
const HRProgramDetailPage = lazyNamed(
  () => import("../features/HRPrograms/HRProgramDetail.page"),
  "HRProgramDetailPage",
);
const CreateProgramPage = lazyNamed(
  () => import("../features/HRPrograms/CreateProgram.page"),
  "CreateProgramPage",
);
const HRProgramsDashboardWireframe = lazyDefault(
  () =>
    import("../features/HRPrograms/wireframes/topleader-hr-programs-wireframe"),
);
const HRProgramsWizardWireframe = lazyDefault(
  () =>
    import("../features/HRPrograms/wireframes/topleader-create-program-wizard"),
);

const LazyFallback = () => (
  <Box sx={{ width: "100%" }}>
    <LinearProgress />
  </Box>
);

const Lazy = ({ children }) => (
  <Suspense fallback={<LazyFallback />}>{children}</Suspense>
);

const Root = (props) => {
  const navigation = useNavigation();

  // Uncomment to test ErrorPage
  // console.log(navigation.some.prop);

  return (
    <Contexts>
      {/* TODO: remove SyncLocalFavorites */}
      <SyncLocalFavorites />
      {navigation.state === "loading" && (
        <Box sx={{ width: "100%" }}>
          <LinearProgress />
        </Box>
      )}
      <Outlet />
    </Contexts>
  );
};

export const router = createHashRouter([
  {
    path: "/",
    element: <Root />,
    errorElement: <ErrorPage />,
    children: [
      { index: true, element: <AuthRedirect /> },
      {
        path: routes.signIn,
        element: (
          <ForbidAuth>
            <SignInPage />
          </ForbidAuth>
        ),
      },
      {
        path: routes.signOut,
        element: <SignOutPage />,
      },
      {
        path: routes.setPassword,
        element: (
          <ForbidAuth>
            <SetPasswordPage />
          </ForbidAuth>
        ),
      },
      {
        path: routes.setPassword_TODO_1_JAKUB_K,
        element: (
          <ForbidAuth>
            <SetPasswordPage />
          </ForbidAuth>
        ),
      },
      {
        path: routes.resetPassword,
        element: (
          <ForbidAuth>
            <SetPasswordPage isExistingUser />
          </ForbidAuth>
        ),
      },
      {
        path: routes.resetPassword_TODO_1_JAKUB_K,
        element: (
          <ForbidAuth>
            <SetPasswordPage isExistingUser />
          </ForbidAuth>
        ),
      },
      {
        path: routes.passwordChanged,
        element: (
          <ForbidAuth>
            <PasswordChangedPage />
          </ForbidAuth>
        ),
      },
      {
        path: routes.forgotPassword,
        element: (
          <ForbidAuth>
            <ForgotPasswordPage />
          </ForbidAuth>
        ),
      },
      {
        path: routes.checkEmail,
        element: (
          <ForbidAuth>
            <CheckEmailPage />
          </ForbidAuth>
        ),
      },
      {
        path: routes.dashboard,
        element: (
          <RequireAuth>
            <DashboardPage />
          </RequireAuth>
        ),
      },
      {
        path: routes.assessment,
        element: (
          <RequireAuth>
            <Lazy>
              <AssessmentPage />
            </Lazy>
          </RequireAuth>
        ),
      },
      {
        path: routes.strengths,
        element: (
          <RequireAuth>
            <Lazy>
              <StrengthsPage />
            </Lazy>
          </RequireAuth>
        ),
      },
      {
        path: routes.setValues,
        element: (
          <RequireAuth>
            <Lazy>
              <SetValuesPage />
            </Lazy>
          </RequireAuth>
        ),
      },
      {
        path: routes.myValues,
        element: (
          <RequireAuth>
            <Lazy>
              <MyValuesPage />
            </Lazy>
          </RequireAuth>
        ),
      },
      {
        path: routes.sessions,
        children: [
          {
            index: true,
            element: (
              <RequireAuth>
                <Lazy>
                  <SessionsPage />
                </Lazy>
              </RequireAuth>
            ),
          },
          {
            path: routes.session,
            element: (
              <RequireAuth>
                <Lazy>
                  <SessionPage />
                </Lazy>
              </RequireAuth>
            ),
          },
          {
            path: routes.startSession,
            element: (
              <RequireAuth>
                <Lazy>
                  <StartSessionPage />
                </Lazy>
              </RequireAuth>
            ),
          },
        ],
      },
      {
        path: routes.coaches,
        element: (
          <RequireAuth rolesDef={rolesDefByRoute[routes.coaches]}>
            <Lazy>
              <CoachesPage />
            </Lazy>
          </RequireAuth>
        ),
      },
      {
        path: routes.mySessions,
        element: (
          <RequireAuth rolesDef={rolesDefByRoute[routes.mySessions]}>
            <Lazy>
              <MySessionsPage />
            </Lazy>
          </RequireAuth>
        ),
      },
      {
        path: routes.settings,
        element: (
          <RequireAuth>
            <Lazy>
              <SettingsPage />
            </Lazy>
          </RequireAuth>
        ),
      },
      {
        path: routes.profile,
        element: (
          <RequireAuth rolesDef={rolesDefByRoute[routes.profile]}>
            <Lazy>
              <UserProfilePage />
            </Lazy>
          </RequireAuth>
        ),
      },
      {
        path: routes.syncSuccess,
        element: (
          <RequireAuth>
            <Lazy>
              <IntegrationResultPage result="success" />
            </Lazy>
          </RequireAuth>
        ),
      },
      {
        path: routes.syncError,
        element: (
          <RequireAuth>
            <Lazy>
              <IntegrationResultPage result="error" />
            </Lazy>
          </RequireAuth>
        ),
      },
      {
        path: routes.help,
        element: (
          <RequireAuth>
            <Lazy>
              <HelpPage />
            </Lazy>
          </RequireAuth>
        ),
      },
      {
        path: routes.getFeedback,
        element: (
          <RequireAuth>
            <Lazy>
              <GetFeedbackPage />
            </Lazy>
          </RequireAuth>
        ),
      },
      {
        path: routes.createFeedbackForm,
        element: (
          <RequireAuth>
            <Lazy>
              <CreateFeedbackPage />
            </Lazy>
          </RequireAuth>
        ),
      },
      {
        path: routes.copyFeedbackForm,
        element: (
          <RequireAuth>
            <Lazy>
              <CreateFeedbackPage isCopy />
            </Lazy>
          </RequireAuth>
        ),
      },
      {
        path: routes.editFeedbackForm,
        element: (
          <RequireAuth>
            <Lazy>
              <CreateFeedbackPage isEdit />
            </Lazy>
          </RequireAuth>
        ),
      },
      {
        path: routes.feedbackResults,
        element: (
          <RequireAuth>
            <Lazy>
              <FeedbackResultsPage />
            </Lazy>
          </RequireAuth>
        ),
      },
      {
        path: routes.externalFeedbackPage,
        element: (
          <Lazy>
            <ExternalFeedbackPage />
          </Lazy>
        ),
      },
      {
        path: routes.sessionsAndMembers,
        element: (
          <RequireAuth rolesDef={rolesDefByRoute[routes.sessionsAndMembers]}>
            <Lazy>
              <SessionsAndMembers />
            </Lazy>
          </RequireAuth>
        ),
      },
      {
        path: routes.team,
        element: (
          <RequireAuth rolesDef={rolesDefByRoute[routes.team]}>
            <Lazy>
              <TeamPage />
            </Lazy>
          </RequireAuth>
        ),
      },
      {
        path: routes.myTeam,
        element: (
          <RequireAuth rolesDef={rolesDefByRoute[routes.myTeam]}>
            <Lazy>
              <MyTeamPage />
            </Lazy>
          </RequireAuth>
        ),
      },
      {
        path: routes.clients,
        element: (
          <RequireAuth rolesDef={rolesDefByRoute[routes.clients]}>
            <Lazy>
              <ClientsPage />
            </Lazy>
          </RequireAuth>
        ),
      },
      {
        path: routes.messages,
        element: (
          <RequireAuth>
            <Lazy>
              <MessagesPage />
            </Lazy>
          </RequireAuth>
        ),
      },
      {
        path: routes.notes,
        element: (
          <RequireAuth>
            <Lazy>
              <NotesPage />
            </Lazy>
          </RequireAuth>
        ),
      },
      {
        path: routes.reports,
        element: (
          <RequireAuth>
            <Lazy>
              <ReportsPage />
            </Lazy>
          </RequireAuth>
        ),
      },
      {
        path: routes.article,
        element: (
          <RequireAuth>
            <Lazy>
              <ArticlePage />
            </Lazy>
          </RequireAuth>
        ),
      },
      {
        path: routes.hrPrograms,
        element: (
          <RequireAuth rolesDef={rolesDefByRoute[routes.hrPrograms]}>
            <Lazy>
              <HRProgramsPage />
            </Lazy>
          </RequireAuth>
        ),
      },
      {
        path: routes.hrProgramDetail,
        element: (
          <RequireAuth rolesDef={rolesDefByRoute[routes.hrProgramDetail]}>
            <Lazy>
              <HRProgramDetailPage />
            </Lazy>
          </RequireAuth>
        ),
      },
      {
        path: routes.createProgram,
        element: (
          <RequireAuth rolesDef={rolesDefByRoute[routes.createProgram]}>
            <Lazy>
              <CreateProgramPage />
            </Lazy>
          </RequireAuth>
        ),
      },
      {
        path: routes.createProgramEdit,
        element: (
          <RequireAuth rolesDef={rolesDefByRoute[routes.createProgramEdit]}>
            <Lazy>
              <CreateProgramPage />
            </Lazy>
          </RequireAuth>
        ),
      },
      {
        // just clickable wireframe, cannot go in production like this, needs to be implemented properly
        path: "/wireframes",
        element: isDevelopment() ? (
          <RequireAuth>
            {/* <Lazy> */}
            <Outlet />
            {/* <HRProgramsDashboardWireframe /> */}
            {/* <HRProgramsWizardWireframe /> */}
            {/* </Lazy> */}
          </RequireAuth>
        ) : null,
        children: [
          {
            path: "hr-programs",
            element: (
              <Lazy>
                <HRProgramsDashboardWireframe />
              </Lazy>
            ),
          },
          {
            path: "create-program",
            element: (
              <Lazy>
                <HRProgramsWizardWireframe />
              </Lazy>
            ),
          },
        ],
      },
    ],
  },
]);
